// Implements: F-IDN-026
/**
 * "Invitar admin" (Plataforma > Organizaciones): the one address an organization's admin is invited
 * with. A new organization has nobody in it, so its first admin arrives this way. The address is
 * checked here before anything is sent (none, or not exactly one address, is said under the field);
 * a refusal of the server is shown in the same place and the dialog stays open with what was typed.
 */

import { useId, useState } from "react";
import type { ReactElement } from "react";

import { useTranslation } from "../../../shared/i18n";
import { Button } from "../../../shared/ui/atoms/Button";
import { Input } from "../../../shared/ui/atoms/Input";
import { Field, errorId } from "../../../shared/ui/molecules/Field";
import { Dialog } from "../../../shared/ui/organisms/Dialog";
import { parseInvitationEmails } from "../model/invitationEmails";
import styles from "./Organization.module.css";

export interface InviteAdminDialogProps {
  readonly organizationName: string;
  readonly pending: boolean;
  /** The server's refusal, already in the person's words, or `null`. */
  readonly problem: string | null;
  readonly onSubmit: (email: string) => void;
  readonly onCancel: () => void;
}

export function InviteAdminDialog({ organizationName, pending, problem, onSubmit, onCancel }: InviteAdminDialogProps): ReactElement {
  const { t } = useTranslation();
  const id = useId();
  const [raw, setRaw] = useState("");
  const [invalid, setInvalid] = useState<string | null>(null);
  const shown = invalid ?? problem ?? undefined;

  function submit(): void {
    if (pending) return;
    const { valid, invalid: rejected } = parseInvitationEmails(raw);
    const [email] = valid;
    if (email === undefined && rejected.length === 0) {
      setInvalid(t("organization.platform.inviteAdmin.required"));
      return;
    }
    if (email === undefined || valid.length > 1 || rejected.length > 0) {
      setInvalid(t("organization.platform.inviteAdmin.invalid"));
      return;
    }
    setInvalid(null);
    onSubmit(email);
  }

  return (
    <Dialog
      open
      title={t("organization.platform.inviteAdmin.title")}
      onClose={onCancel}
      footer={
        <>
          <Button variant="secondary" onClick={onCancel}>
            {t("common.action.cancel")}
          </Button>
          <Button variant="primary" loading={pending} onClick={submit}>
            {t("organization.platform.inviteAdmin.submit")}
          </Button>
        </>
      }
    >
      <div className={styles.form}>
        <div>
          <span className={styles.label}>{t("organization.platform.inviteAdmin.organization")}</span>
          <p className={styles.strong}>{organizationName}</p>
        </div>
        <p className={styles.status}>{t("organization.platform.inviteAdmin.intro")}</p>
        <Field id={id} label={t("organization.platform.inviteAdmin.email")} error={shown}>
          <Input
            id={id}
            type="email"
            autoComplete="off"
            value={raw}
            placeholder={t("organization.platform.inviteAdmin.placeholder")}
            invalid={shown !== undefined}
            describedBy={shown === undefined ? undefined : errorId(id)}
            onChange={(value) => {
              setRaw(value);
              setInvalid(null);
            }}
            onEnter={submit}
          />
        </Field>
      </div>
    </Dialog>
  );
}
